<template>
  <div class="config-manager">
    <div v-if="backendError" class="config-error">
      <span>{{ backendError }}</span>
      <button type="button" @click="fetchBackendConfig">重试</button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue';
import { message } from 'ant-design-vue';
import { DEFAULT_LONGEST_SILENCE_MS } from '@/utils/cozeRealtimeSession';

/**
 * 后端配置接口
 */
export interface BackendConfig {
  user_id: number;
  bot_id: string;
  access_token: string;
  voice_id: string;
  video_device_id: string;
  suppress_stationary_noise: boolean;
  suppress_non_stationary_noise: boolean;
  avatar_image: string;
  longest_silence_ms?: number;
}

/**
 * 配置管理组件
 * 负责获取和管理后端配置，包括缓存处理
 */
export default defineComponent({
  name: 'ConfigManager',
  emits: ['config-loaded'],
  setup(props, { emit }) {
    // 智能体ID
    const botId = ref('');
    // 访问令牌
    const accessToken = ref('');
    // 语音ID
    const voiceId = ref('');
    // 视频设备ID
    const videoDeviceId = ref('');
    // 配置是否加载完成
    const configLoaded = ref(false);
    // 后端错误信息
    const backendError = ref('');
    // 是否抑制平稳噪声
    const suppressStationaryNoise = ref(false);
    // 是否抑制非平稳噪声
    const suppressNonStationaryNoise = ref(false);
    // 头像图片
    const avatarImage = ref('');

    /**
     * 获取后端配置
     * @returns Promise<void>
     */
    const fetchBackendConfig = async (): Promise<void> => {
      try {
        backendError.value = '';

        // 先清除旧的缓存，确保每次都从服务器获取最新配置
        localStorage.removeItem('backendConfig');

        const response = await fetch('/api/config', {
          credentials: 'include',
        });
        if (!response.ok) {
          const err = await response.json().catch(() => ({
            detail: '配置服务不可用',
          }));
          throw new Error(err.detail || 'Failed to fetch config');
        }
        const data: BackendConfig = await response.json();
        botId.value = data.bot_id;
        accessToken.value = data.access_token;
        voiceId.value = data.voice_id;
        videoDeviceId.value = data.video_device_id;
        suppressStationaryNoise.value = !!data.suppress_stationary_noise;
        suppressNonStationaryNoise.value = !!data.suppress_non_stationary_noise;
        avatarImage.value = data.avatar_image || '/xingxiaonge6c.gif';
        configLoaded.value = true;
        backendError.value = '';

        // 缓存配置到本地存储（作为备份）
        localStorage.setItem('backendConfig', JSON.stringify(data));
        if (process.env.NODE_ENV === 'development') {
        }

        emit('config-loaded', {
          userId: String(data.user_id),
          botId: botId.value,
          accessToken: accessToken.value,
          voiceId: voiceId.value,
          videoDeviceId: videoDeviceId.value,
          suppressStationaryNoise: suppressStationaryNoise.value,
          suppressNonStationaryNoise: suppressNonStationaryNoise.value,
          avatarImage: avatarImage.value,
          longestSilenceMs:
            typeof data.longest_silence_ms === 'number' &&
            data.longest_silence_ms > 0
              ? data.longest_silence_ms
              : DEFAULT_LONGEST_SILENCE_MS,
        });
        message.success('专用大模型加载成功');
      } catch (e: any) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Backend fetch error:', e);
        }
        // 发生错误时，不使用缓存，直接显示错误
        // 这样可以确保用户在切换后能够看到正确的配置状态
        backendError.value = e.message || '配置加载失败';
        configLoaded.value = false;
        message.error(backendError.value);
      }
    };

    onMounted(() => {
      fetchBackendConfig();
    });

    return {
      configLoaded,
      backendError,
      fetchBackendConfig,
    };
  },
});
</script>

<style scoped>
.config-manager {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.config-error {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  color: var(--error-color);
  background: rgba(255, 82, 82, 0.12);
  border: 1px solid rgba(255, 82, 82, 0.35);
  border-radius: 4px;
  box-shadow: var(--shadow-md);
}

.config-error button {
  height: 30px;
  padding: 0 12px;
  color: var(--primary-color);
  background: transparent;
  border: 1px solid rgba(64, 158, 255, 0.45);
  border-radius: 4px;
  cursor: pointer;
}
</style>
